/**
 * <exportacoes-mapa> — seção de exportações da home (T4): mapa-múndi com
 * origem pulsando e 3 arcos animados até EUA/Alemanha/China.
 *
 * O destaque hover/focus/toque dos arcos é 100% CSS (:has() no palco, ver
 * {% stylesheet %} da section) — funciona por teclado sem nenhum listener
 * aqui. Este componente só cobre o que CSS não resolve sozinho:
 *
 * 1. Alternar aria-pressed no click dos botões-destino (equivalente ao
 *    :hover em telas sem ponteiro fino — toque não dispara :hover), um
 *    ativo por vez — e limpar esse estado por dismissal (pointerdown fora
 *    do host ou Escape, mesmo padrão de assets/mapa-operacoes.js: listeners
 *    de document ligados no connectedCallback e desligados no
 *    disconnectedCallback). O pressed não se limpa sozinho ao passar o
 *    mouse por outro destino — fica "preso" até um dos dois gestos; a
 *    coerência visual disso (só um destino plenamente destacado por vez)
 *    é resolvida em CSS, ver comentário no {% stylesheet %} da section.
 * 2. Contadores "3 países · 3 continentes": count-up de 0 até o valor já
 *    publicado no HTML, disparado por IntersectionObserver — só "arma" o
 *    estado zerado depois de confirmar que vai poder repor o valor final
 *    (mesma lógica fail-open de sections/manifesto.liquid: JS quebrado ou
 *    ausente nunca deixa o contador travado em 0). Dispara uma única vez
 *    via flag própria (this.contado) — o observer nunca desconecta sozinho.
 * 3. Loop dos arcos e do pulso da origem (classe exportacoes-mapa--visivel)
 *    liga/desliga junto com isIntersecting: a seção sai da viewport e as
 *    animações pausam de novo, sem gastar ciclo fora de tela.
 *
 * prefers-reduced-motion corta os itens 2 e 3: os contadores mantêm o valor
 * final do HTML e os arcos/pulso ficam parados no estado estático (sem
 * --visivel, que é justamente o gatilho do @keyframes).
 */
class ExportacoesMapa extends HTMLElement {
  connectedCallback() {
    this.destinos = Array.from(this.querySelectorAll('.exportacoes-mapa__destino'));
    this.aoClicarDestino = this.aoClicarDestino.bind(this);
    this.destinos.forEach((botao) => botao.addEventListener('click', this.aoClicarDestino));

    // Dismissal do aria-pressed: clique fora do host ou Escape. Independe
    // de reduced-motion/IntersectionObserver, por isso fica fora do bloco
    // abaixo.
    this.aoTeclado = this.aoTeclado.bind(this);
    this.aoApontarFora = this.aoApontarFora.bind(this);
    document.addEventListener('keydown', this.aoTeclado);
    document.addEventListener('pointerdown', this.aoApontarFora);

    this.contadores = Array.from(this.querySelectorAll('[data-contador]'));
    this.contado = false;
    const semMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (semMotion || typeof IntersectionObserver === 'undefined') {
      // Reduced motion ou sem suporte: o HTML já mostra o valor final
      // ("3 países · 3 continentes") e os arcos já nascem estáticos —
      // nada a armar.
      return;
    }

    try {
      this.contadores.forEach((numero) => {
        // ??= idempotente: em reconexão antes do primeiro intersect, o
        // textContent já pode estar zerado por uma passada anterior deste
        // mesmo connectedCallback — reler aqui perderia o alvo real.
        numero.dataset.alvo ??= numero.textContent.trim();
        numero.textContent = '0';
      });
      this.observer = new IntersectionObserver(
        (entradas) => {
          for (const entrada of entradas) {
            // Observer fica conectado pra sempre (nunca desconecta sozinho)
            // e alterna a classe --visivel nos dois sentidos — ela é quem
            // liga/desliga o loop dos arcos e do pulso da origem (CSS). O
            // count-up é o único efeito de mão única, gateado pela flag.
            this.classList.toggle('exportacoes-mapa--visivel', entrada.isIntersecting);
            if (entrada.isIntersecting && !this.contado) {
              this.contado = true;
              this.contadores.forEach((numero) => this.contarAte(numero, Number(numero.dataset.alvo)));
            }
          }
        },
        { threshold: 0.35 }
      );
      this.observer.observe(this);
    } catch (erro) {
      // Observer falhou depois de já termos zerado os contadores no DOM:
      // repõe o valor final na hora pra não deixar "0" travado na tela.
      this.contadores.forEach((numero) => {
        numero.textContent = numero.dataset.alvo ?? numero.textContent;
      });
    }
  }

  disconnectedCallback() {
    this.destinos.forEach((botao) => botao.removeEventListener('click', this.aoClicarDestino));
    document.removeEventListener('keydown', this.aoTeclado);
    document.removeEventListener('pointerdown', this.aoApontarFora);
    this.observer?.disconnect();
  }

  aoClicarDestino(evento) {
    const alvo = evento.currentTarget;
    const jaAtivo = alvo.getAttribute('aria-pressed') === 'true';
    this.limparPressionado();
    if (!jaAtivo) alvo.setAttribute('aria-pressed', 'true');
  }

  get destinoPressionado() {
    return this.destinos.find((botao) => botao.getAttribute('aria-pressed') === 'true') ?? null;
  }

  limparPressionado() {
    this.destinos.forEach((botao) => botao.setAttribute('aria-pressed', 'false'));
  }

  aoTeclado(evento) {
    if (evento.key !== 'Escape') return;
    const pressionado = this.destinoPressionado;
    if (!pressionado) return;
    this.limparPressionado();
    pressionado.focus();
  }

  aoApontarFora(evento) {
    const pressionado = this.destinoPressionado;
    if (!pressionado) return;
    const grupo = pressionado.closest('.exportacoes-mapa__destino-grupo');
    if (grupo?.contains(evento.target)) return;
    this.limparPressionado();
  }

  contarAte(elemento, alvoFinal) {
    const inicio = performance.now();
    const duracao = 1100;
    const passo = (agora) => {
      const progresso = Math.min((agora - inicio) / duracao, 1);
      const suavizado = 1 - (1 - progresso) ** 3;
      elemento.textContent = String(Math.round(alvoFinal * suavizado));
      if (progresso < 1) requestAnimationFrame(passo);
    };
    requestAnimationFrame(passo);
  }
}

if (!customElements.get('exportacoes-mapa')) {
  customElements.define('exportacoes-mapa', ExportacoesMapa);
}
